/*
 * Search drawer enhancements
 *
 * 1. Pre-search suggestions — when "Show Shopify search suggestions" is on, the
 *    trending terms rendered in the drawer are treated as seeds. On the first
 *    open we ask /search/suggest.json for each seed, merge the unique queries
 *    that come back and replace the terms list with them.
 *
 * 2. Predictive search result counts — predictive search only reports the few
 *    resources it was asked for, so it cannot say how many products, pages or
 *    articles actually match. Store-wide totals are fetched per resource type
 *    from the `api-search-count` section and filled into the group headings and
 *    the actions bar.
 *
 * 3. Quick View buttons on the result cards, which the theme only wires up for
 *    content inside a section.
 *
 * Everything caches per query so a given lookup only ever happens once.
 */

(function () {
  'use strict';

  const selectors = {
    termsList: '[data-popular-searches-terms][data-fetch-suggestions]',
    popdown: 'search-popdown',
    searchFormWrapper: 'predictive-search, search-form',
    form: 'form[data-search-form]',
    input: 'input[type="search"]',
    link: '[data-popular-searches-link]',
  };

  const attributes = {
    loaded: 'data-suggestions-loaded',
  };

  const storageKey = 'theme:popular-searches:suggestions';

  // signature -> Promise<string[]>, so parallel drawers share a single request
  const inFlight = new Map();

  function getSeedTerms(list) {
    try {
      const seeds = JSON.parse(list.dataset.seedTerms || '[]');
      if (!Array.isArray(seeds)) return [];
      return seeds.map((seed) => String(seed).trim()).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function getTermsLimit(list) {
    const limit = parseInt(list.dataset.termsLimit, 10);
    return Number.isNaN(limit) || limit < 1 ? 10 : limit;
  }

  function getSuggestUrl(term, limit) {
    const routes = (window.theme && window.theme.routes) || {};
    const base = routes.predictiveSearchUrl || '/search/suggest';
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'query',
      'resources[limit]': String(limit),
    });

    return `${base}.json?${params.toString()}`;
  }

  function readCache(signature) {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(storageKey));
      if (cached && cached.signature === signature && Array.isArray(cached.terms)) {
        return cached.terms;
      }
    } catch (error) {
      // sessionStorage unavailable or holding stale data — fall through to fetch
    }

    return null;
  }

  function writeCache(signature, terms) {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({signature: signature, terms: terms}));
    } catch (error) {
      // Quota or private browsing — the in-memory cache still applies
    }
  }

  function fetchSuggestions(seed, limit) {
    return fetch(getSuggestUrl(seed, limit))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const queries = data && data.resources && data.resources.results && data.resources.results.queries;
        return Array.isArray(queries) ? queries.map((query) => query.text).filter(Boolean) : [];
      })
      .catch(() => []);
  }

  // Merge the per-seed results in seed order, dropping case-insensitive duplicates
  function mergeUniqueTerms(termGroups, limit) {
    const seen = new Set();
    const merged = [];

    termGroups.forEach((terms) => {
      terms.forEach((term) => {
        const key = term.trim().toLowerCase();
        if (!key || seen.has(key) || merged.length >= limit) return;

        seen.add(key);
        merged.push(term.trim());
      });
    });

    return merged;
  }

  function loadSuggestions(seeds, limit) {
    const signature = `${limit}::${seeds.join('|')}`;
    const cached = readCache(signature);

    if (cached) return Promise.resolve(cached);
    if (inFlight.has(signature)) return inFlight.get(signature);

    const request = Promise.all(seeds.map((seed) => fetchSuggestions(seed, limit)))
      .then((termGroups) => {
        const terms = mergeUniqueTerms(termGroups, limit);
        if (terms.length) writeCache(signature, terms);
        return terms;
      })
      .finally(() => inFlight.delete(signature));

    inFlight.set(signature, request);

    return request;
  }

  function searchUrl(term) {
    const routes = (window.theme && window.theme.routes) || {};
    const base = routes.searchUrl || '/search';

    return `${base}?q=${encodeURIComponent(term)}`;
  }

  function renderTerms(list, terms) {
    const prefix = list.dataset.termsIdPrefix || 'popularSearchesTerm';

    list.innerHTML = terms
      .map((term, index) => {
        const item = document.createElement('div');
        item.className = 'predictive-search__item';
        item.id = `${prefix}-${index + 1}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');

        const link = document.createElement('a');
        link.className = 'predictive-search__link';
        link.href = searchUrl(term);
        link.tabIndex = -1;
        link.setAttribute('data-popular-searches-link', '');
        link.textContent = term;

        item.appendChild(link);

        return item.outerHTML;
      })
      .join('');
  }

  // The theme binds its own handlers to the server rendered links at startup, so
  // the replacements need their own. Only links we created are handled here.
  function bindTermClicks(list) {
    list.addEventListener('click', (event) => {
      const link = event.target.closest(selectors.link);
      if (!link || !list.contains(link)) return;

      const popdown = list.closest(selectors.popdown);
      const wrapper = popdown && popdown.querySelector(selectors.searchFormWrapper);
      const form = popdown && popdown.querySelector(selectors.form);
      const input = form && form.querySelector(selectors.input);

      if (!form || !input) return;

      event.preventDefault();
      input.value = link.textContent;

      if (wrapper && typeof wrapper.submit === 'function') {
        wrapper.submit();
      } else {
        form.submit();
      }
    });
  }

  function initTermsList(list) {
    const seeds = getSeedTerms(list);
    if (!seeds.length) return;

    const limit = getTermsLimit(list);
    const trigger = list.closest(selectors.popdown) || document;

    const onFirstOpen = () => {
      if (list.hasAttribute(attributes.loaded)) return;
      list.setAttribute(attributes.loaded, 'true');

      loadSuggestions(seeds, limit).then((terms) => {
        // Keep the merchant's seed terms when there is nothing to swap in
        if (!terms.length) return;

        renderTerms(list, terms);
        bindTermClicks(list);
      });
    };

    trigger.addEventListener('focusin', onFirstOpen, {once: true});
  }

  /* ---------------------------------------------------------------------
   * Predictive search result count
   * ------------------------------------------------------------------- */

  const countSelectors = {
    results: '[data-predictive-search-results]',
    predictiveSearch: 'predictive-search',
    count: '[data-predictive-search-count]',
    searchCount: '[data-search-count]',
    input: 'input[type="search"]',
  };

  const quickViewSelectors = {
    button: '[data-button-quick-view]',
  };

  const totalPlaceholder = '__TOTAL__';

  const totalCache = new Map(); // query -> total
  const totalInFlight = new Map(); // query -> Promise<number|null>

  function getCountUrl(query, type) {
    const routes = (window.theme && window.theme.routes) || {};
    const base = routes.searchUrl || '/search';
    const params = new URLSearchParams({
      q: query,
      type: type,
      'options[prefix]': 'last',
      section_id: 'api-search-count',
    });

    return `${base}?${params.toString()}`;
  }

  function fetchTotal(query, type) {
    // Several elements can ask for the same type, so key the cache on both and
    // let them share one request
    const key = `${type}::${query.toLowerCase()}`;

    if (totalCache.has(key)) return Promise.resolve(totalCache.get(key));
    if (totalInFlight.has(key)) return totalInFlight.get(key);

    const request = fetch(getCountUrl(query, type))
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => {
        if (!text) return null;

        const parsed = new DOMParser().parseFromString(text, 'text/html').querySelector(countSelectors.searchCount);
        const total = parsed ? parseInt(parsed.dataset.searchCount, 10) : NaN;

        if (Number.isNaN(total)) return null;

        totalCache.set(key, total);
        return total;
      })
      .catch(() => null)
      .finally(() => totalInFlight.delete(key));

    totalInFlight.set(key, request);

    return request;
  }

  function updateResultCounts(results) {
    const countElements = results.querySelectorAll(countSelectors.count);
    if (!countElements.length) return;

    const wrapper = results.closest(countSelectors.predictiveSearch);
    const input = wrapper && wrapper.querySelector(countSelectors.input);
    const query = input ? input.value.trim() : '';

    if (!query) return;

    countElements.forEach((countElement) => {
      const type = countElement.dataset.searchType;
      const template = countElement.dataset.countTemplate;

      // Groups with no store-wide equivalent, such as suggestions, keep the
      // plain count they were rendered with
      if (!type || !template) return;

      const shown = parseInt(countElement.dataset.shownCount, 10) || 0;

      fetchTotal(query, type).then((total) => {
        // Results for a newer query may have replaced this element in the meantime
        if (total === null || !countElement.isConnected) return;
        if (input.value.trim() !== query) return;

        // Only claim a total when there is genuinely more than what's on
        // screen. An equal total means every match is already displayed, and a
        // lower one means the lookup didn't resolve — "(10 of 10)" and
        // "(10 of 0)" both read as broken, so keep the plain count instead.
        if (total <= shown) return;

        countElement.textContent = template.replace(totalPlaceholder, String(total));
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Quick View buttons on search result cards
   * ------------------------------------------------------------------- */

  // The theme binds Quick View per section, and the search drawer isn't one, so
  // its cards have to be wired up here. Buttons already carry a product URL, so
  // if the theme's popup isn't available they simply stay links.
  function initQuickView(container) {
    const QuickViewPopup = window.theme && window.theme.QuickViewPopup;

    if (typeof QuickViewPopup !== 'function') return;
    if (!container.querySelector(quickViewSelectors.button)) return;

    // The constructor skips buttons it has already bound
    new QuickViewPopup(container);
  }

  // The theme replaces the whole results container on every render, so a
  // childList observer gives us exactly one callback per set of results.
  function initResults(results) {
    new MutationObserver(() => {
      updateResultCounts(results);
      initQuickView(results);
    }).observe(results, {childList: true});
  }

  function init() {
    document.querySelectorAll(selectors.termsList).forEach(initTermsList);
    document.querySelectorAll(countSelectors.results).forEach(initResults);
    // Pre-search product cards are in the page from the start
    document.querySelectorAll(selectors.popdown).forEach(initQuickView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
